import { useState, useEffect, useRef, useCallback } from 'react';
import { subscribePlan, applyContentDiff } from './planStore';
import { stateToContent, contentToState } from './planModel';
import {
  diffContentToWrites, mergeRemoteContent, remoteConfirms,
} from './planSync';

/**
 * Collaborative persistence for one plan (plans/{planId}), a drop-in replacement
 * for the solo tool's local state + wholesale save. It exposes the SAME surface
 * the workbench expects — { state, setState, mutate, readOnly, loading } — so the
 * existing render tree works unchanged, but:
 *
 *   • state comes LIVE from onSnapshot (contentToState), so a collaborator's edit
 *     appears here a moment later.
 *   • mutate() applies the edit optimistically to local state AND pushes only the
 *     changed field paths (diffContentToWrites → applyContentDiff), debounced so a
 *     burst of keystrokes is one write.
 *   • incoming snapshots are merged WITHOUT stomping the field you're editing:
 *     any path with an in-flight (not-yet-confirmed) local edit keeps the local
 *     value; everything else takes remote. See mergeRemoteContent.
 *   • ui / collapsed / per-card _collapsed are per-viewer view state — kept local
 *     and never written (stateToContent strips them, so they never enter a diff).
 *
 * setState is the raw local setter (for view-only toggles like collapse); it
 * updates the on-screen state but writes nothing.
 */
const clone = (v) => JSON.parse(JSON.stringify(v));

// A plan doc is { ...ownershipMeta, ...content }. Pull just the content half.
function contentFromDoc(doc) {
  const {
    ownerUid, ownerEmail, memberEmails, memberUids, title, createdAt, updatedAt, ...content
  } = doc;
  return content;
}

export default function useCollabPlan({ planId, authUser, enabled = true, writeDelay = 400 } = {}) {
  const [state, setStateRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null); // { ownerUid, ownerEmail, memberEmails }

  const stateRef = useRef(null);
  const contentRef = useRef(null);            // authoritative local content
  const inflightRef = useRef(new Map());      // path → local value | DELETE_SENTINEL (unconfirmed)
  const dirtyRef = useRef({});                // writes accumulated since last flush
  const timerRef = useRef(null);
  const viewRef = useRef({ ui: { examples: true }, collapsed: {} }); // per-viewer, survives merges
  const collapsedByIdRef = useRef(new Map()); // id → _collapsed, per-viewer card state

  // Re-apply per-viewer view state onto a fresh state derived from content.
  const applyView = useCallback((s) => {
    s.ui = viewRef.current.ui;
    s.collapsed = viewRef.current.collapsed;
    const m = collapsedByIdRef.current;
    [s.cast, s.rel, s.factions].forEach((list) => list.forEach((it) => { if (m.get(it.id)) it._collapsed = true; }));
    return s;
  }, []);

  // Capture per-viewer view state out of a state the caller just produced.
  const captureView = (s) => {
    viewRef.current = { ui: s.ui, collapsed: s.collapsed };
    const m = collapsedByIdRef.current; m.clear();
    [s.cast, s.rel, s.factions].forEach((list) => list.forEach((it) => { if (it._collapsed) m.set(it.id, true); }));
  };

  const flush = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    const updates = dirtyRef.current;
    dirtyRef.current = {};
    if (Object.keys(updates).length && planId) {
      applyContentDiff(planId, updates).catch((e) => setError(e));
    }
  }, [planId]);

  // ── Live subscription ──
  useEffect(() => {
    if (!enabled || !planId) { setLoading(false); return undefined; }
    setLoading(true); setNotFound(false); setError(null);
    const unsub = subscribePlan(
      planId,
      (doc) => {
        if (!doc) { setNotFound(true); setLoading(false); return; }
        setMeta({ ownerUid: doc.ownerUid, ownerEmail: doc.ownerEmail || '', memberEmails: doc.memberEmails || [] });
        const remoteContent = contentFromDoc(doc);
        // Retire any in-flight path the server has now echoed back.
        inflightRef.current.forEach((val, path) => {
          if (remoteConfirms(remoteContent, path, val)) inflightRef.current.delete(path);
        });
        // Merge remote, keeping the fields we're still editing.
        const merged = mergeRemoteContent(remoteContent, inflightRef.current);
        contentRef.current = merged;
        const nextState = applyView(contentToState(merged));
        stateRef.current = nextState;
        setStateRaw(nextState);
        setLoading(false);
      },
      (err) => { setError(err); setLoading(false); },
    );
    return () => {
      unsub && unsub();
      flush(); // persist anything still pending on unmount / plan switch
    };
  }, [planId, enabled, applyView, flush]);

  const isOwner = !!(meta && authUser && meta.ownerUid === authUser.uid);
  // Editing an existing plan is free for any member (rules gate on membership,
  // not entitlement) — so the collab editor is never read-only on entitlement.
  // It's only inert before the first snapshot loads, or on a hard error.
  const readOnly = !!error || loading || !state;

  // ── Content mutation: optimistic local + scoped debounced write ──
  const mutate = useCallback((fn) => {
    const prev = stateRef.current;
    if (!prev || error) return;
    const next = clone(prev);
    fn(next);
    captureView(next);
    stateRef.current = next;
    setStateRaw(next);

    const prevContent = contentRef.current || stateToContent(prev);
    const nextContent = stateToContent(next);
    contentRef.current = nextContent;
    const writes = diffContentToWrites(prevContent, nextContent);
    const paths = Object.keys(writes);
    if (paths.length) {
      paths.forEach((p) => { inflightRef.current.set(p, writes[p]); dirtyRef.current[p] = writes[p]; });
      if (!timerRef.current) timerRef.current = setTimeout(flush, writeDelay);
    }
  }, [error, flush, writeDelay]);

  // ── View-only setter: updates the screen, writes nothing (collapse toggles) ──
  const setState = useCallback((updater) => {
    const prev = stateRef.current;
    if (!prev) return;
    const next = typeof updater === 'function' ? updater(prev) : updater;
    captureView(next);
    stateRef.current = next;
    setStateRaw(next);
  }, []);

  return {
    state, setState, mutate, readOnly, loading, notFound, error, meta, isOwner,
    flushNow: flush,
  };
}
